/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    CHANNEL_NUMBER_MAX_LENGTH,
    ENTER_CHANNEL_NUMBER_DISPLAY_TIME,
    MAX_CHANNEL_NUMBER_DISPLAY_TIME,
} from '../../../config';
import {
    fetchAndUpdateChannelsFromChannelNumber,
    stopEnterChannelNumber,
} from '../../../store/guide/enterchannelnumber/enterchannelnumber.reducers';
import { AppUtils } from '../../../core/AppUtils';
import { RootState } from '../../../store/root.reducers';
import { TimeBar } from '../TimeBar';
import { DeviceModel } from '../../../core/device/DeviceModel';

import './GridGuideHeader.scss';

const GridGuideHeader = (): JSX.Element => {
    const dispatch = useDispatch();
    const { startTime, currentTimestamp, isPastGuideMode, isGuideRowsServiceFail } = useSelector(
        (state: RootState) => state.gridGuide,
    );
    const { isChannelsServiceFail } = useSelector((state: RootState) => state.gridGuide);
    const { enteredChannelNumber, isEnterChannelNumberActive } = useSelector(
        (state: RootState) => state.enterChannelNumber,
    );

    useEffect(() => {
        let enterChannelNumberTimerId: any;
        const enterChannelNumberThreshold =
            enteredChannelNumber?.length < CHANNEL_NUMBER_MAX_LENGTH
                ? ENTER_CHANNEL_NUMBER_DISPLAY_TIME
                : MAX_CHANNEL_NUMBER_DISPLAY_TIME;
        if (isEnterChannelNumberActive) {
            enterChannelNumberTimerId = setTimeout(() => {
                if (enteredChannelNumber?.length > 0)
                    dispatch(fetchAndUpdateChannelsFromChannelNumber(enteredChannelNumber));
                dispatch(stopEnterChannelNumber());
            }, enterChannelNumberThreshold);
        }

        return () => {
            clearTimeout(enterChannelNumberTimerId);
        };
    }, [enteredChannelNumber, isEnterChannelNumberActive]);

    const gridHeaderCellsTime = AppUtils.generateTimeTexts(currentTimestamp);
    const gridHeaderCellDate = AppUtils.formatDayAndDate(
        currentTimestamp,
        DeviceModel.getTVProperties().getLang(),
    );
    const isServiceFailed = isChannelsServiceFail || isGuideRowsServiceFail;
    const isTimebarVisible = currentTimestamp.getTime() <= startTime.getTime() && !isServiceFailed;

    return (
        <div className="header-container">
            {isTimebarVisible ? <TimeBar /> : null}
            <div className="left-blank-cell"></div>
            {!isPastGuideMode ? (
                <div className="date-cell-container">
                    <div
                        className={`${
                            isEnterChannelNumberActive ? 'enter-channel-number' : 'date-text'
                        }`}
                        data-testid="guide-day-date"
                    >
                        {isEnterChannelNumberActive
                            ? `${enteredChannelNumber}_`
                            : gridHeaderCellDate}
                    </div>
                </div>
            ) : null}
            {gridHeaderCellsTime.map((time, index) => {
                return (
                    <div key={`time-cell-container-${index}`} className="time-cell-container">
                        <div className="time-text" data-testid="guide-time-text">
                            {time}
                        </div>
                    </div>
                );
            })}
            {isPastGuideMode ? (
                <div className="date-cell-container">
                    <div
                        className={`${
                            isEnterChannelNumberActive ? 'enter-channel-number' : 'date-text'
                        }`}
                        data-testid="guide-day-date"
                    >
                        {isEnterChannelNumberActive
                            ? `${enteredChannelNumber}_`
                            : gridHeaderCellDate}
                    </div>
                </div>
            ) : null}
            <div className="right-blank-cell"></div>
        </div>
    );
};

export default GridGuideHeader;
